import React, { useRef, useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon } from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ViewColumnRoundedIcon from '@mui/icons-material/ViewColumnRounded';
import { useGoTo } from '../GoToContext/useGoTo';
import { FormattedMessage, useIntl } from 'react-intl';

const containerStyles = {
  position: 'absolute' as const,
  top: 16,
  right: 16,
  zIndex: 10,
};

const buttonStyles = (theme) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.background.paper,
    opacity: 0.6,
  },
});

const MapToolsMenu = () => {
  const intl = useIntl();
  const { captureMapScreenshot, exportLegend } = useGoTo();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleScreenshot = async () => {
    setOpen(false);
    setBusy(true);
    try {
      await captureMapScreenshot();
    } catch (err) {
      console.error('Map screenshot failed', err);
    } finally {
      setBusy(false);
    }
  };

  const handleExportLegend = async (orientation: 'vertical' | 'horizontal') => {
    setOpen(false);
    setBusy(true);
    try {
      await exportLegend(orientation);
    } catch (err) {
      console.error('Legend export failed', err);
    } finally {
      setBusy(false);
    }
  };

  const handleExportVerticalLegend = () => handleExportLegend('vertical');
  const handleExportHorizontalLegend = () => handleExportLegend('horizontal');

  return (
    <div style={containerStyles}>
      <IconButton
        ref={anchorRef}
        size="small"
        sx={buttonStyles}
        disabled={busy}
        onClick={() => setOpen((o) => !o)}
        title={intl.formatMessage({
          id: 'map.tools.title',
          defaultMessage: 'Map tools',
        })}
        aria-label={intl.formatMessage({
          id: 'map.tools.title',
          defaultMessage: 'Map tools',
        })}
      >
        <BuildIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleScreenshot}>
          <ListItemIcon>
            <PhotoCameraIcon fontSize="small" />
          </ListItemIcon>
          <FormattedMessage
            id="map.tools.screenshot"
            defaultMessage="Screenshot"
          />
        </MenuItem>
        <MenuItem onClick={handleExportVerticalLegend}>
          <ListItemIcon>
            <FormatListBulletedIcon fontSize="small" />
          </ListItemIcon>
          <FormattedMessage
            id="map.tools.exportVerticalLegend"
            defaultMessage="Export vertical legend"
          />
        </MenuItem>
        <MenuItem onClick={handleExportHorizontalLegend}>
          <ListItemIcon>
            <ViewColumnRoundedIcon fontSize="small" />
          </ListItemIcon>
          <FormattedMessage
            id="map.tools.exportHorizontalLegend"
            defaultMessage="Export horizontal legend"
          />
        </MenuItem>
      </Menu>
    </div>
  );
};

export { MapToolsMenu };
